import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const InvoiceListPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/records`);
      setInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSelectInvoice = (id) => {
    setSelectedInvoices(prev => 
      prev.includes(id) 
        ? prev.filter(invoiceId => invoiceId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/billing/invoice/records/${id}`);
      if (response.data.success) {
        setSuccessMessage('Invoice deleted successfully');
        fetchInvoices();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to delete invoice:', err);
      setError('Failed to delete invoice');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedInvoices.length === 0) {
      setError('Please select invoices to delete');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedInvoices.length} invoice(s)?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/billing/invoice/records/delete-multiple`, {
        ids: selectedInvoices
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setSelectedInvoices([]);
        fetchInvoices();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to delete invoices:', err);
      setError('Failed to delete invoices');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL invoices? This action cannot be undone.')) {
      return;
    }

    const allIds = invoices.map(invoice => invoice.id);
    try {
      const response = await axios.post(`${API_BASE_URL}/billing/invoice/records/delete-multiple`, {
        ids: allIds
      });
      if (response.data.success) {
        setSuccessMessage('All invoices deleted successfully');
        setSelectedInvoices([]);
        fetchInvoices();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to delete all invoices:', err);
      setError('Failed to delete all invoices');
      setTimeout(() => setError(''), 3000);
    }
  };


  if (loading) {
    return <div className="container"><p>Loading invoices...</p></div>;
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Invoice History</h2>
        <div>
          {selectedInvoices.length > 0 && (
            <>
              <button 
                className="btn btn-danger me-2"
                onClick={handleDeleteSelected}
              >
                Delete Selected ({selectedInvoices.length})
              </button>
            </>
          )}
          {invoices.length > 0 && (
            <button 
              className="btn btn-outline-danger me-2"
              onClick={handleDeleteAll}
            >
              Delete All
            </button>
          )}
          <Link to="/invoice" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Generate New Invoice
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {invoices.length === 0 ? (
        <div className="alert alert-info">
          No invoices found. <Link to="/invoice">Generate your first invoice</Link>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                  />
                </th>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Client</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => handleSelectInvoice(invoice.id)}
                    />
                  </td>
                  <td>{invoice.invoice_number || invoice.filename?.replace('.pdf', '') || `HUHC${invoice.id}`}</td>
                  <td>{formatDate(invoice.created_at)}</td>
                  <td>{invoice.bill_to_name || `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim()}</td>
                  <td>{invoice.service_description?.substring(0, 50)}...</td>
                  <td>{invoice.hours && invoice.unit_price ? formatCurrency(invoice.hours * invoice.unit_price) : 'N/A'}</td>
                  <td>{invoice.due_date}</td>
                  <td>
                    {invoice.email_sent ? (
                      <span className="badge bg-success">Email Sent</span>
                    ) : (
                      <span className="badge bg-secondary">N/A</span>
                    )}
                  </td>
                  <td>
                    <Link 
                      to={`/invoice-preview/${invoice.id}`} 
                      className="btn btn-sm btn-outline-primary me-1"
                      title="View"
                    >
                      <i className="bi bi-eye"></i>
                    </Link>
                    <Link 
                      to={`/invoice/edit/${invoice.id}`} 
                      className="btn btn-sm btn-outline-warning me-1"
                      title="Edit"
                    >
                      <i className="bi bi-pencil"></i>
                    </Link>
                    <button
                      onClick={() => handleDeleteSingle(invoice.id)}
                      className="btn btn-sm btn-outline-danger me-1"
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                    {invoice.s3_url && (
                      <a 
                        href={invoice.s3_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        title="Download PDF"
                      >
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceListPage;